import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type BillingPlanResult, type ListSalesOrdersResult } from '@whinerp/client';
import { formatMoney } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/**
 * Deposits, staged payments and pro-formas (G-122), for each order on the deal.
 *
 * A stage is invoiced as a draft and issued from Invoices like any other, or
 * requested first with a pro-forma - which is not counted as owed until it is
 * converted, when the customer has paid it.
 */

const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';

export function BillingPlanPanel({ dealId }: { dealId: string }): JSX.Element | null {
  const t = useT();
  const { can } = useSession();
  const enabled = useEntitlement('F-080').enabled && can('revenue.view');
  const orders = useQuery<ListSalesOrdersResult, ApiError>({
    queryKey: ['sales-orders', 'deal', dealId],
    queryFn: () => api.listSalesOrders({ query: { dealId } }),
    enabled,
  });
  if (!enabled || !orders.data) return null;
  const live = orders.data.orders.filter((order) => order.status !== 'cancelled');
  if (live.length === 0) return null;
  return (
    <Panel data-region="billing-plan">
      <div className="flex flex-col gap-4">
        <SectionLabel>{t('billingPlan.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('billingPlan.help')}</p>
        {live.map((order) => (
          <OrderPlan key={order.orderId} orderId={order.orderId} number={order.number} currency={order.currency} />
        ))}
      </div>
    </Panel>
  );
}

function OrderPlan({ orderId, number, currency }: { orderId: string; number: string; currency: string }): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const manage = can('revenue.manage');
  const key = ['billing-plan', orderId];
  const plan = useQuery<BillingPlanResult, ApiError>({ queryKey: key, queryFn: () => api.billingPlan({ path: { orderId } }) });
  const [draft, setDraft] = useState({ label: '', percent: '' });
  const [said, setSaid] = useState<string | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: key });

  const add = useMutation({
    mutationFn: () => api.addBillingStage({ path: { orderId }, body: draft }),
    onSuccess: () => {
      setDraft({ label: '', percent: '' });
      refresh();
    },
  });
  const act = useMutation({
    mutationFn: async (input: { kind: 'invoice' | 'proforma' | 'convert'; id: string }) => {
      if (input.kind === 'invoice') {
        const result = await api.invoiceBillingStage({ path: { stageId: input.id } });
        return t('billingPlan.invoiced', { values: { amount: formatMoney(result.gross, format, { currency }) } });
      }
      if (input.kind === 'proforma') {
        const result = await api.raiseProforma({ path: { stageId: input.id } });
        return t('billingPlan.requested', {
          values: { number: result.proforma.number, amount: formatMoney(result.proforma.gross, format, { currency }) },
        });
      }
      await api.convertProforma({ path: { proformaId: input.id } });
      return t('billingPlan.converted');
    },
    onSuccess: (message) => {
      setSaid(message);
      refresh();
    },
  });

  if (!plan.data) return <div />;
  return (
    <section className="flex flex-col gap-2" data-billing-order={orderId}>
      <h3 className="text-dense text-ink">
        {t('billingPlan.order', { values: { number, planned: plan.data.plannedPercent } })}
      </h3>
      <ul className="flex flex-col gap-1">
        {plan.data.stages.map((stage) => {
          const billed = stage.invoiceId !== null && stage.invoiceStatus !== 'void';
          return (
            <li key={stage.stageId} data-billing-stage={stage.stageId}
              className="flex flex-wrap items-center gap-2 rounded-chip bg-sunken px-3 py-2 text-dense">
              <span className="text-ink">{stage.label}</span>
              <span className="text-ink-meta">{stage.percent}%</span>
              {billed ? (
                <span className="text-meta text-ink-meta" data-stage-invoiced>
                  {t('billingPlan.stageInvoiced', { values: { number: stage.invoiceNumber ?? t('billingPlan.draft') } })}
                </span>
              ) : null}
              {stage.proformaNumber ? (
                <span className="text-meta text-ink-meta" data-stage-proforma>
                  {t('billingPlan.stageRequested', { values: { number: stage.proformaNumber } })}
                </span>
              ) : null}
              {manage && !billed ? (
                <span className="ms-auto flex gap-2">
                  {stage.proformaId ? (
                    <Button size="small" data-stage-convert={stage.stageId}
                      onClick={() => act.mutate({ kind: 'convert', id: stage.proformaId! })}>
                      {t('billingPlan.convert')}
                    </Button>
                  ) : (
                    <>
                      <Button size="small" data-stage-proforma-raise={stage.stageId}
                        onClick={() => act.mutate({ kind: 'proforma', id: stage.stageId })}>
                        {t('billingPlan.requestProforma')}
                      </Button>
                      <Button size="small" intent="primary" data-stage-invoice={stage.stageId}
                        onClick={() => act.mutate({ kind: 'invoice', id: stage.stageId })}>
                        {t('billingPlan.invoice')}
                      </Button>
                    </>
                  )}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
      {said ? <p className="text-meta text-ink-meta" data-billing-said>{said}</p> : null}
      {act.error ? <p className="text-dense text-danger-text">{errorText(act.error as ApiError)}</p> : null}
      {manage ? (
        <form data-billing-add className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            add.mutate();
          }}>
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('billingPlan.label')}
            <input className={INPUT} required maxLength={120} data-billing-label value={draft.label}
              onChange={(event) => setDraft({ ...draft, label: event.target.value })} />
          </label>
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('billingPlan.percent')}
            <input className={`${INPUT} w-24`} required inputMode="decimal" data-billing-percent value={draft.percent}
              onChange={(event) => setDraft({ ...draft, percent: event.target.value })} />
          </label>
          <Button size="small" type="submit" data-billing-add-submit disabled={add.isPending}>{t('billingPlan.add')}</Button>
          {add.error ? (
            <p className="w-full text-dense text-danger-text" data-billing-add-error>{errorText(add.error as ApiError)}</p>
          ) : null}
        </form>
      ) : null}
    </section>
  );
}
